import { useMutation, useQueryClient } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { ConversationResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import {
  refreshConversationAuthorityAfterMutationError,
  refreshInquiryAuthority,
} from "../../../common/hooks/conversationCacheAuthority";
import {
  installConfirmedTombstone,
  reconcileMessagePages,
} from "./messageCacheAuthority";

export function useDeleteMessage(conversationId: string, communityId?: string) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    onError: (error) =>
      refreshConversationAuthorityAfterMutationError(
        queryClient,
        conversationId,
        communityId,
        error,
      ),
    mutationFn: async (messageId) => {
      await socialApi.deleteMessage(messageId);
    },
    onSuccess: (_response, messageId) => {
      void refreshInquiryAuthority(queryClient, conversationId);
      installConfirmedTombstone(queryClient, conversationId, messageId);
      queryClient.setQueryData<ConversationResponse[]>(
        queryKeys.social.conversations(),
        (current) =>
          current?.map((conversation) => {
            const preview = conversation.last_message;
            if (preview?.message_id !== messageId) return conversation;
            return {
              ...conversation,
              last_message: {
                ...preview,
                content: null,
                is_deleted: true,
                has_shared_post: false,
                has_attachments: false,
              },
            };
          }),
      );
      void reconcileMessagePages(queryClient, conversationId);
      void queryClient.invalidateQueries({
        queryKey: communityId
          ? queryKeys.social.communityChannels(communityId)
          : queryKeys.social.conversations(),
      });
    },
  });
}
